'use client';

import React, { useEffect, useState } from 'react';
import { classes } from '@/utils/classes/classes';
import './styles/default.scss';

type SpinningNumberProps = {
    value: number;
    duration?: number;
};

export const SpinningNumber: React.FC<SpinningNumberProps> = ({ value, duration = 2000 }) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldTransform, setShouldTransform] = useState(false);
    const [isAnimationComplete, setIsAnimationComplete] = useState(false);

    useEffect(() => {
        // Reset transform state first to ensure animation triggers
        setShouldTransform(false);
        setIsAnimating(false);

        // Use requestAnimationFrame to ensure the reset is applied before starting animation
        const rafId = requestAnimationFrame(() => {
            setIsAnimating(true);
            setShouldTransform(true);
        });

        const timer = setTimeout(() => {
            setIsAnimating(false);
            setIsAnimationComplete(true);
        }, duration);

        return () => {
            cancelAnimationFrame(rafId);
            clearTimeout(timer);
        };
    }, [value, duration]);

    const renderDigits = () => {
        const formattedValue = value
            .toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: true
            })
            .replace(/,/g, ',');

        const targetDigits = formattedValue.split('');

        return targetDigits.map((targetDigit, index) => {
            const target = Number.parseInt(targetDigit);
            if (Number.isNaN(target)) {
                return (
                    <div key={`${targetDigit}-${index}`} className="digit-viewport">
                        <span className="digit">{targetDigit}</span>
                    </div>
                );
            }

            const isSpinUp = target >= 5;
            const sequence = isSpinUp
                ? Array.from({ length: target + 1 }, (_, i) => i)
                : [0, ...Array.from({ length: 9 - target + 1 }, (_, i) => 9 - i)];

            return (
                <div key={`${targetDigit}-${index}`} className="digit-viewport">
                    <div
                        className={classes('digit-roller', shouldTransform && 'animating')}
                        data-direction={isSpinUp ? 'up' : 'down'}
                        style={
                            {
                                '--sequence-length': sequence.length - 1,
                                transition: isAnimationComplete ? 'none' : `transform ${duration}ms ease-in-out`
                            } as React.CSSProperties
                        }>
                        {sequence.map((num) => (
                            <span key={num} className="digit">
                                {num}
                            </span>
                        ))}
                    </div>
                </div>
            );
        });
    };

    return (
        <div className={classes('spinning-number', isAnimating && 'animating')}>
            <div className="digits">{renderDigits()}</div>
        </div>
    );
};
